import { BitmapText, Texture } from "pixi.js";
import { SymbolView } from "@slot/presentation";
import type { Log } from "@slot/presentation";
import { parseSymbolId } from "../rules/symbols";
import type { CoinShine, CoinShines } from "../scene/CoinShines";
import type { Collector, Collectors } from "../scene/Collectors";
import { ART_SCALE, COIN_IDLE_LAYOUT, COLLECTOR_LAYOUT } from "../scene/layout";
import { BITMAP_FONTS, setCoinText } from "../views/textStyles";

/**
 * How a value sits on a coin's face, in the coin image's pixels (384 square, as tools/art imports them): centred, at
 * most `maxWidth` wide (the coin's inner face); `fontSize` the Roboto coin font's, the artist's golden digits drawing at
 * their share of it (`COIN_DIGITS`: 64, the mock-up's "4.00"). On the delivery's red collect coin, above its name
 * (`collectY`); on the artist's collector, centred on its ball at the artist's 100 mock-up px (`collector`).
 */
const FACE = { maxWidth: 230, fontSize: 96, collectY: -18, collector: { y: 0, fontSize: 87 } } as const;

/** A paid cell's tint (the bonus's total took its amount): the coin darkened, its number with it, as 3 Coin Volcanoes'. */
const PAID_TINT = 0x6e6e6e;

/** Formats a coin's value for its face: the money formatter without the currency symbol (the mock-up's "4.00"). */
export type CoinValueFormatter = (minor: string) => string;

/**
 * A grid cell that can draw a coin's value on its face: the shell's `SymbolView` plus one bitmap label in the content
 * node (so it scales, squashes and rolls with the coin). The value is part of the symbol id (`C:250`, `COL:1200`), so
 * every bind — the board's, a rolling lane's filler, a collect taking its sum — sets the label from the id alone. A plain
 * coin shines (the artist's idle coin: a sprite of one of its variants, `CoinShines`) in place of its still image, a
 * collect coin shows the artist's collector, lent while the cell shows it (`Collectors`), in place of the red coin; each
 * given back as the cell shows another symbol or is put back, and before they are in, the still images.
 */
export class CoinSymbolView extends SymbolView {
  private readonly valueLabel: BitmapText;
  private readonly format: CoinValueFormatter;
  private readonly collectors: Collectors | null;
  private collector: Collector | null = null;
  private readonly shines: CoinShines | null;
  private shine: CoinShine | null = null;

  public constructor(input: { format: CoinValueFormatter; log: Log; collectors?: Collectors; shines?: CoinShines }) {
    super(input.log);
    this.format = input.format;
    this.collectors = input.collectors ?? null;
    this.shines = input.shines ?? null;
    this.valueLabel = new BitmapText({ text: "", style: { fontFamily: BITMAP_FONTS.coin, fontSize: FACE.fontSize }, label: "CoinValue" });
    this.valueLabel.anchor.set(0.5);
    this.valueLabel.visible = false;
    this.contentNode.addChild(this.valueLabel);
  }

  public override setSymbolId(symbolId: string): void {
    super.setSymbolId(symbolId);
    this.setPaid(false);
    const symbol = parseSymbolId(symbolId);
    if (symbol?.kind === "collect") this.showCollector();
    else this.releaseCollector();
    if (symbol?.kind === "coin") this.showShine();
    else this.releaseShine();
    const minor = symbol?.kind === "coin" ? symbol.valueMinor : symbol?.kind === "collect" ? symbol.valueMinor : null;
    this.showValue(minor);
  }

  /** Whether this cell shows the artist's collector (the dev hook's and the tests' read). */
  public get showsCollector(): boolean {
    return this.collector !== null;
  }

  /** The idle coin's variant this cell's coin shines with; null while it shows none (the tests' read). */
  public get shineVariant(): number | null {
    return this.shine?.variant ?? null;
  }

  /**
   * Shows `minor` on the face (a collect counting up shows its running sum before its id carries it): on a coin
   * centred, on a collect coin where its art has room for it.
   */
  public showValue(minor: string | null): void {
    if (minor === null) {
      this.valueLabel.visible = false;
      return;
    }
    const collect = parseSymbolId(this.getSymbolId() ?? "")?.kind === "collect";
    const face = !collect ? { y: 0, fontSize: FACE.fontSize } : this.collector ? FACE.collector : { y: FACE.collectY, fontSize: FACE.fontSize };
    setCoinText(this.valueLabel, this.format(minor), face.fontSize);
    this.valueLabel.position.set(0, face.y);
    const natural = this.valueLabel.width / (this.valueLabel.scale.x || 1);
    const scale = Math.min(1, FACE.maxWidth / Math.max(1, natural));
    this.valueLabel.scale.set(scale, scale);
    this.valueLabel.visible = true;
  }

  /** Whether the bonus's total has taken this cell's amount: the cell darkened (another symbol bound clears it). */
  public get paid(): boolean {
    return this.contentNode.tint === PAID_TINT;
  }

  public setPaid(paid: boolean): void {
    this.contentNode.tint = paid ? PAID_TINT : 0xffffff;
  }

  /** This collect coin starts collecting: its collector's bigger flare (the red coin has none). */
  public activateCollector(): void {
    this.collector?.activate();
  }

  /** A value arrives in this collect coin: its collector pulses. */
  public pulseCollector(): void {
    this.collector?.collect();
  }

  /** Its collector and its shine back: the cell is put back (the grid's release) or goes. */
  public releaseArt(): void {
    this.releaseCollector();
    this.releaseShine();
  }

  public override destroy(): void {
    this.releaseArt();
    super.destroy();
  }

  /** Its collector back to the pool: the cell shows another symbol, or is put back. */
  private releaseCollector(): void {
    const collector = this.collector;
    if (!collector) return;
    this.collector = null;
    this.collectors?.giveBack(collector);
  }

  /** The coin's shine in its still image's place, under the value, at its artboard's size in design units (kept while it stays a coin). */
  private showShine(): void {
    this.shine ??= this.shines?.take() ?? null;
    const shine = this.shine;
    if (!shine) return;
    this.symbolSprite.texture = Texture.EMPTY;
    const scale = this.contentNode.scale.x || 1;
    shine.sprite.position.set(0, 0);
    shine.sprite.setSize((COIN_IDLE_LAYOUT.artboard.width * ART_SCALE) / scale, (COIN_IDLE_LAYOUT.artboard.height * ART_SCALE) / scale);
    shine.sprite.removeFromParent();
    this.contentNode.addChildAt(shine.sprite, this.contentNode.getChildIndex(this.valueLabel));
  }

  /** Its shine back: the cell shows another symbol, or is put back. */
  private releaseShine(): void {
    const shine = this.shine;
    if (!shine) return;
    this.shine = null;
    this.shines?.giveBack(shine);
  }

  /** The collector in the red coin's place, under the value, at its artboard's size in design units. */
  private showCollector(): void {
    this.collector ??= this.collectors?.lend() ?? null;
    const collector = this.collector;
    if (!collector) return;
    this.symbolSprite.texture = Texture.EMPTY;
    const scale = this.contentNode.scale.x || 1;
    collector.sprite.position.set(0, 0);
    collector.sprite.setSize((COLLECTOR_LAYOUT.artboard.width * ART_SCALE) / scale, (COLLECTOR_LAYOUT.artboard.height * ART_SCALE) / scale);
    // Out first: the label's place is then where it goes (a rebind finds it here already).
    collector.sprite.removeFromParent();
    this.contentNode.addChildAt(collector.sprite, this.contentNode.getChildIndex(this.valueLabel));
  }
}
